import * as _ from 'lodash';
import { useState } from 'react';
import { useDispatch } from 'react-redux';

import { Action, type Permission, ResourceType } from '../../../../typings/permissions';
import { useAddToast } from '../../../contexts/ToastContext';
import { errorMessageFromResponse, type FailedFetchResponse } from '../../../helpers/errors';
import { useOrganizationPackageLabels } from '../../../hooks/api/packageLabels';
import { useBulkPackageLabels } from '../../../hooks/api/packageLabels/useBulkPackageLabels';
import queryClient from '../../../ReactQuery/client';
import * as DependenciesV2Store from '../../../redux/dependenciesV2';
import useHasPermission from '../../../shared/permissions/useHasPermission';
import { useTabs } from '../../generic';
import type { SelectOption } from '../../../types';

import { Tab, type VersionedLabelOption } from './types';
import type { BulkPackageLabelState } from './packageLabelStateTypes';

export type DependencyOption = SelectOption;

/**
 * State hook for bulk package label assignment.
 * Manages selected dependencies and label assignments across all scopes.
 */
export const useBulkPackageLabelState = (parentProjectLocator: string): BulkPackageLabelState => {
  const tabs = useTabs({ tabIds: Object.values(Tab), defaultTab: Tab.Global });
  const addToast = useAddToast();
  const hasPermission = useHasPermission();
  const dispatch = useDispatch();

  // Permissions
  const globalPermission: Permission = { action: Action.Set, resourceType: ResourceType.PackageLabel };
  const localPermission: Permission = {
    action: Action.SetLocal,
    resourceType: ResourceType.ProjectPackageLabel,
    projectId: parentProjectLocator,
  };
  const hasGlobalPermission = hasPermission(globalPermission);
  const hasLocalPermission = hasPermission(localPermission);
  const hasNoPermission: boolean = !hasGlobalPermission && !hasLocalPermission;

  // Fetch organization labels
  const { data: labels, isLoading: isLoadingLabels } = useOrganizationPackageLabels();

  // Bulk assignment mutation
  const bulkPackageLabels = useBulkPackageLabels();

  // State for selected dependencies
  const [selectedDependencies, setSelectedDependencies] = useState<DependencyOption[]>([]);

  // State for selected labels in each scope
  const [globalLabelsSelected, setGlobalLabelsSelected] = useState<VersionedLabelOption>({ all: new Set() });
  const [projectLabelsSelected, setProjectLabelsSelected] = useState<VersionedLabelOption>({ all: new Set() });
  const [revisionLabelsSelected, setRevisionLabelsSelected] = useState<VersionedLabelOption>({ all: new Set() });

  // State for version specificity toggle in each scope
  const [shouldUseGlobalSpecificVersion, setShouldUseGlobalSpecificVersion] = useState(false);
  const [shouldUseProjectSpecificVersion, setShouldUseProjectSpecificVersion] = useState(false);
  const [shouldUseRevisionSpecificVersion, setShouldUseRevisionSpecificVersion] = useState(false);

  const onSaveSuccess = () => {
    addToast({ type: 'success', content: 'Labels assigned successfully' });
    queryClient.invalidateQueries({ queryKey: ['packageLabels'] });
    dispatch(DependenciesV2Store.actions.preFetchLoading());
    dispatch(DependenciesV2Store.actions.fetchDependencies());
  };

  /**
   * Save global scope label assignments.
   * For each label and version combination, call the bulk API.
   */
  const onSaveGlobal = async () => {
    const packageLocators = selectedDependencies.map((dep) => dep.value);

    if (packageLocators.length === 0) {
      addToast({ type: 'error', content: 'Please select at least one dependency' });
      return;
    }

    try {
      // Build array of promises for all API calls
      const promises = Object.entries(globalLabelsSelected).flatMap(([, labelIds]) =>
        Array.from(labelIds).map((labelId) =>
          bulkPackageLabels.mutateAsync({
            packageLocators,
            labelId,
            scope: 'org',
            scopeId: undefined,
            shouldUseSpecificVersion: shouldUseGlobalSpecificVersion,
          }),
        ),
      );

      // Execute all API calls in parallel
      await Promise.all(promises);

      onSaveSuccess();
      // Reset selections
      setGlobalLabelsSelected({ all: new Set() });
      setShouldUseGlobalSpecificVersion(false);
    } catch (error: unknown) {
      const errorMessage = errorMessageFromResponse(error as FailedFetchResponse);
      addToast({ type: 'error', content: `Error assigning global labels: ${errorMessage}` });
    }
  };

  /**
   * Save project scope label assignments.
   * For each label and version combination, call the bulk API.
   */
  const onSaveProject = async (projectLocator: string) => {
    const packageLocators = selectedDependencies.map((dep) => dep.value);

    if (packageLocators.length === 0) {
      addToast({ type: 'error', content: 'Please select at least one dependency' });
      return;
    }

    try {
      // Build array of promises for all API calls
      const promises = Object.entries(projectLabelsSelected).flatMap(([, labelIds]) =>
        Array.from(labelIds).map((labelId) =>
          bulkPackageLabels.mutateAsync({
            packageLocators,
            labelId,
            scope: 'project',
            scopeId: projectLocator,
            shouldUseSpecificVersion: shouldUseProjectSpecificVersion,
          }),
        ),
      );

      // Execute all API calls in parallel
      await Promise.all(promises);

      onSaveSuccess();
      // Reset selections
      setProjectLabelsSelected({ all: new Set() });
      setShouldUseProjectSpecificVersion(false);
    } catch (error: unknown) {
      const errorMessage = _.isObject(error) && 'text' in error ? (error as { text?: string }).text : 'Unknown error';
      addToast({ type: 'error', content: `Error assigning project labels: ${errorMessage}` });
    }
  };

  /**
   * Save revision scope label assignments.
   * For each label and version combination, call the bulk API.
   */
  const onSaveRevision = async (parentRevisionLocator: string) => {
    const packageLocators = selectedDependencies.map((dep) => dep.value);

    if (packageLocators.length === 0) {
      addToast({ type: 'error', content: 'Please select at least one dependency' });
      return;
    }

    try {
      // Build array of promises for all API calls
      const promises = Object.entries(revisionLabelsSelected).flatMap(([, labelIds]) =>
        Array.from(labelIds).map((labelId) =>
          bulkPackageLabels.mutateAsync({
            packageLocators,
            labelId,
            scope: 'revision',
            scopeId: parentRevisionLocator,
            shouldUseSpecificVersion: shouldUseRevisionSpecificVersion,
          }),
        ),
      );

      // Execute all API calls in parallel
      await Promise.all(promises);

      onSaveSuccess();
      // Reset selections
      setRevisionLabelsSelected({ all: new Set() });
      setShouldUseRevisionSpecificVersion(false);
    } catch (error: unknown) {
      const errorMessage =
        _.isObject(error) && 'data' in error ? (error.data as { message?: string })?.message : 'Unknown error';
      addToast({ type: 'error', content: `Error assigning revision labels: ${errorMessage}` });
    }
  };

  return {
    mode: 'bulk' as const,
    tabs,
    selectedDependencies,
    setSelectedDependencies,
    globalLabelsSelected,
    setGlobalLabelsSelected,
    projectLabelsSelected,
    setProjectLabelsSelected,
    revisionLabelsSelected,
    setRevisionLabelsSelected,
    shouldUseGlobalSpecificVersion,
    setShouldUseGlobalSpecificVersion,
    shouldUseProjectSpecificVersion,
    setShouldUseProjectSpecificVersion,
    shouldUseRevisionSpecificVersion,
    setShouldUseRevisionSpecificVersion,
    labels,
    isLoadingLabels,
    onSaveGlobal,
    onSaveProject,
    onSaveRevision,
    hasGlobalPermission,
    hasLocalPermission,
    hasNoPermission,
  };
};
